// ── Player classes ──
// LocalPlayer: the client-authoritative representation used for prediction.
// RemotePlayer: server-driven entity with interpolation and a visual mesh.

import * as THREE from 'three';
import { type WeaponId, WEAPONS } from '../core/config';
import { SkinLoader } from '../cosmetics/skin-loader';
import type { WeaponSkinLoader } from '../cosmetics/weapon-skin-loader';
import { buildSkinMesh } from '../cosmetics/skin-builders';
import { lerp, lerpAngle } from '../utils/math';
import { createBlobShadow, releaseBlobShadow } from '../renderer/contact-shadow';
import { attachTPPWeapon, removeTPPWeapon, removeTPPBomb } from './tpp-weapon';
import { ClimbAnimator, type ClimbAnimState } from './animation';

// ── Interpolation buffer entry ──
export interface RemotePlayerState {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  timestamp: number;
}

// ── Local Player ──

export class LocalPlayer {
  id = 0;
  name = 'Player';
  skinId = 0;

  // Transform
  readonly position = new THREE.Vector3(0, 0, 0);
  readonly velocity = new THREE.Vector3(0, 0, 0);
  yaw = 0;   // radians, rotation around Y axis
  pitch = 0; // radians, clamped ±π/2

  // Gameplay state
  health = 100;
  armor = 0;
  team = 0; // 0=unassigned, 1=T, 2=CT (set from server snapshot)
  isCrouching = false;
  isGrounded = false;

  // Slide state
  isSliding = false;
  slideTimer = 0;
  slideCooldownTimer = 0;
  slideDirection = { x: 0, z: 0 };

  // Bhop / Slide hop state
  bhopChain = 0;               // Consecutive successful slide-jump chains
  bhopMultiplier = 1.0;        // Current speed multiplier from bhop (1.0 = no bonus)
  bhopFromSlide = false;       // True if player jumped out of a slide (in bhop air)
  bhopLandingTimer = 0;        // Window after landing to chain into next slide

  // Smooth eye height (for crouch/slide camera transitions)
  eyeHeightSmooth = 1.6;

  // Tagging (hit slowdown) — mirrored from server for prediction
  tagSlowdown = 0;
  tagTimer = 0;

  // Weapon state
  currentWeapon: WeaponId = 'glock';
  ammo = 0;
  reserveAmmo = 0;

  // F-GP-013 (audits/2026-05-07): per-weapon ammo cache so swapping back to
  // a half-empty mag does NOT flash a full one until the next server
  // snapshot reconciles. Keyed by WeaponId; entries are written as we
  // switch AWAY from a weapon and read on switch IN. Cleared on respawn().
  private weaponAmmoCache = new Map<WeaponId, { ammo: number; reserveAmmo: number }>();

  // Scoreboard
  kills = 0;
  deaths = 0;

  constructor() {
    this.equipWeapon(this.currentWeapon);
  }

  /**
   * Set weapon and restore ammo counts from the per-weapon cache, falling
   * back to the config magazine on first switch. Stashes the OUTGOING
   * weapon's ammo state so the next switch back replays the same counts.
   * Server snapshots are still authoritative — this only smooths over the
   * ~50-100ms window before the snapshot reconciles.
   */
  equipWeapon(id: WeaponId): void {
    // Stash the outgoing weapon's counts so a future switch-back replays
    // them. Skip when ammo/reserveAmmo are 0 (initial state) — those don't
    // represent "fired half a mag" state.
    if (this.currentWeapon !== id && (this.ammo > 0 || this.reserveAmmo > 0)) {
      this.weaponAmmoCache.set(this.currentWeapon, {
        ammo: this.ammo,
        reserveAmmo: this.reserveAmmo,
      });
    }
    this.currentWeapon = id;
    const cached = this.weaponAmmoCache.get(id);
    if (cached) {
      this.ammo = cached.ammo;
      this.reserveAmmo = cached.reserveAmmo;
    } else {
      const cfg = WEAPONS[id];
      this.ammo = cfg.magazine;
      this.reserveAmmo = cfg.reserveAmmo;
    }
  }

  /** Quick check: is the player alive? */
  get alive(): boolean {
    return this.health > 0;
  }

  /** Reset to spawn state. */
  respawn(x: number, y: number, z: number): void {
    this.position.set(x, y, z);
    this.velocity.set(0, 0, 0);
    this.yaw = 0;
    this.pitch = 0;
    this.health = 100;
    this.armor = 0;
    this.isCrouching = false;
    this.isGrounded = false;
    this.isSliding = false;
    this.slideTimer = 0;
    this.slideCooldownTimer = 0;
    this.slideDirection = { x: 0, z: 0 };
    this.bhopChain = 0;
    this.bhopMultiplier = 1.0;
    this.bhopFromSlide = false;
    this.bhopLandingTimer = 0;
    this.eyeHeightSmooth = 1.6;
    this.tagSlowdown = 0;
    this.tagTimer = 0;
    // F-GP-013: drop per-weapon ammo cache on respawn so the next switch
    // produces a fresh full magazine instead of replaying mid-round counts.
    this.weaponAmmoCache.clear();
    this.equipWeapon(this.currentWeapon);
  }
}

// ── Remote Player ──

const INTERP_BUFFER_SIZE = 16;
// Render remote players this many ms behind real-time so that the bracketing
// snapshot pair always exists when the render frame samples it. With server
// ticking at 64 Hz (~15.6 ms/snapshot) and clients rendering at 30–144 fps,
// 100 ms gives ~6 ticks of headroom for network jitter and frame-rate drift.
// Without this delay, the previous code lerped between only the two newest
// snapshots using wall-clock time, which causes visible position jitter at
// low frame rates because t jumps discontinuously as new snapshots arrive
// between render frames.
const INTERP_DELAY_MS = 100;
const WALK_SWING = 0.5; // radians max limb swing
const WALK_SPEED = 8;   // rad/s walk-phase increment
const BREATHE_AMP = 0.008; // idle breathing amplitude

export class RemotePlayer {
  id = 0;
  name = 'Remote';

  // Render position (interpolated)
  readonly position = new THREE.Vector3(0, 0, 0);
  yaw = 0;
  pitch = 0;

  // Health for HUD hit-marker / kill-feed purposes
  health = 100;

  // Team assignment mirrors the LocalPlayer encoding: 0=unassigned, 1=T, 2=CT.
  // Consumed by spectator-xray and other team-colored overlays.
  team = 0;

  // State flags (received from server snapshots)
  isCrouching = false;
  isSliding = false;

  // Airborne detection for animation
  private isGroundedRemote = true;
  private prevY = 0;

  // Skin support
  skinId = 0;
  currentWeapon: WeaponId = 'glock';
  currentWeaponSkinId = 0;

  // G6: mirrors server HasBomb so the TPP C4 prop is attached on state change,
  // not every snapshot. game-network-handlers flips it when isBombCarrier changes.
  tppBombAttached = false;
  private mixer: THREE.AnimationMixer | null = null;
  private isGlbSkin = false;
  private weaponSkinLoader: WeaponSkinLoader | null = null;

  // Visual representation
  mesh: THREE.Group | null = null;

  // Blob contact shadow
  private blobShadow: THREE.Mesh | null = null;

  // Pivot references for procedural animation
  private rightArmPivot: THREE.Group | null = null;
  private leftArmPivot: THREE.Group | null = null;
  private rightLegPivot: THREE.Group | null = null;
  private leftLegPivot: THREE.Group | null = null;
  private torsoMesh: THREE.Mesh | null = null;

  // Animation state
  private walkPhase = 0;
  private lastPosition = new THREE.Vector3();
  private isWalking = false;
  private lastInterpTime = 0;

  // F-GP-001 — Canned ladder + mantle animation overlay driven by Protocol v7
  // bits (isOnLadder / isMantling / mantleT). Updated each animate() tick
  // from the most recent snapshot via setClimbState(); produces pose params
  // applied on top of the procedural walk pose.
  private climbAnimator = new ClimbAnimator();
  private climbState: ClimbAnimState = { position: { x: 0, y: 0, z: 0 } };

  // Interpolation buffer (ring buffer of recent server states)
  private buffer: RemotePlayerState[] = [];

  /** Push a new authoritative state from the server. */
  updateFromServer(state: RemotePlayerState): void {
    this.buffer.push(state);
    // Keep buffer bounded
    if (this.buffer.length > INTERP_BUFFER_SIZE) {
      this.buffer.shift();
    }
  }

  /** Clear the interpolation buffer (e.g. on death to prevent stale lerp on respawn). */
  clearBuffer(): void {
    this.buffer.length = 0;
  }

  /** Update the weapon shown on this player's body. */
  equipWeapon(weaponId: WeaponId, weaponSkinId = this.currentWeaponSkinId): void {
    this.currentWeapon = weaponId;
    this.currentWeaponSkinId = weaponSkinId;
    if (this.mesh) {
      attachTPPWeapon(this.mesh, weaponId, {
        skinId: weaponSkinId,
        skinLoader: this.weaponSkinLoader,
      });
    }
  }

  setWeaponSkinSystem(loader: WeaponSkinLoader | null): void {
    this.weaponSkinLoader = loader;
  }

  /**
   * F-GP-001 — Push the latest Protocol v7 climb/mantle bits to the
   * ClimbAnimator. Called from game-network-handlers each snapshot. Position
   * is the authoritative server position for drift-correction purposes.
   */
  setClimbState(state: ClimbAnimState): void {
    this.climbState = state;
  }

  /**
   * Interpolate the remote player's position toward the buffered snapshots.
   * Uses a fixed render lag (INTERP_DELAY_MS) and picks the two snapshots
   * that bracket the lagged render time, so interpolation alpha varies
   * smoothly with wall-clock progress regardless of render frame rate.
   * @param _alpha Unused (previously game loop sub-tick fraction — incorrect).
   *               F-GP-017 (audits/2026-05-07): kept optional for backward
   *               compat; new callers should pass nothing.
   */
  interpolate(_alpha?: number): void {
    const len = this.buffer.length;
    if (len === 0) return;

    if (len === 1) {
      const s = this.buffer[0];
      this.position.set(s.x, s.y, s.z);
      this.yaw = s.yaw;
      this.pitch = s.pitch;
    } else {
      const renderTime = performance.now() - INTERP_DELAY_MS;

      // Find the bracketing pair (a, b) such that a.timestamp <= renderTime <= b.timestamp.
      // Default to the two oldest if renderTime is older than the buffer's start, and
      // to the two newest if renderTime is past the buffer's end.
      let a = this.buffer[0];
      let b = this.buffer[1];
      if (renderTime >= this.buffer[len - 1].timestamp) {
        a = this.buffer[len - 2];
        b = this.buffer[len - 1];
      } else {
        for (let i = 0; i < len - 1; i++) {
          if (this.buffer[i].timestamp <= renderTime && renderTime <= this.buffer[i + 1].timestamp) {
            a = this.buffer[i];
            b = this.buffer[i + 1];
            break;
          }
        }
      }

      const span = b.timestamp - a.timestamp;
      const t = span > 0 ? Math.min(Math.max((renderTime - a.timestamp) / span, 0), 1) : 1;

      this.position.set(
        lerp(a.x, b.x, t),
        lerp(a.y, b.y, t),
        lerp(a.z, b.z, t),
      );
      this.yaw = lerpAngle(a.yaw, b.yaw, t);
      this.pitch = lerp(a.pitch, b.pitch, t);
    }

    // Sync mesh transform
    if (this.mesh) {
      this.mesh.position.copy(this.position);
      this.mesh.rotation.set(0, this.yaw, 0);
    }

    // Update blob shadow opacity (fade when airborne)
    if (this.blobShadow) {
      const opacity = this.isGroundedRemote ? 0.35 : Math.max(0, 0.35 - Math.abs(this.position.y - this.prevY) * 2);
      (this.blobShadow.material as THREE.MeshBasicMaterial).opacity = opacity;
    }

    // Compute dt and run procedural animation
    const now = performance.now() / 1000; // seconds
    if (this.lastInterpTime === 0) {
      // First frame: initialize time reference, skip animation to avoid huge dt
      this.lastInterpTime = now;
      return;
    }
    const dt = Math.min(now - this.lastInterpTime, 0.1);
    this.lastInterpTime = now;
    if (dt > 0) {
      this.animate(dt);
    }
  }

  /**
   * Procedural walk/idle animation.
   * Detects movement, swings limbs, adds breathing bob.
   */
  private animate(dt: number): void {
    // Detect movement
    const dx = this.position.x - this.lastPosition.x;
    const dz = this.position.z - this.lastPosition.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    this.isWalking = dist > 0.001;
    this.lastPosition.copy(this.position);

    if (this.isWalking) {
      // Advance walk phase
      this.walkPhase += WALK_SPEED * dt;
    } else {
      // Dampen walkPhase toward nearest rest position (0 or PI)
      const nearestRest = Math.round(this.walkPhase / Math.PI) * Math.PI;
      this.walkPhase += (nearestRest - this.walkPhase) * Math.min(1, 10 * dt);
    }

    const swing = Math.sin(this.walkPhase) * WALK_SWING;

    // Legs: right forward when swing > 0, left forward when swing < 0
    if (this.rightLegPivot) {
      this.rightLegPivot.rotation.x = swing;
    }
    if (this.leftLegPivot) {
      this.leftLegPivot.rotation.x = -swing;
    }

    // Arms swing opposite to same-side legs (Z axis for walk bob)
    if (this.leftArmPivot) {
      this.leftArmPivot.rotation.x = this.pitch * 0.3;
      this.leftArmPivot.rotation.z = swing;
    }
    // Right arm: pitch aiming on X axis, walk swing on Z axis (separate axes prevent jitter)
    if (this.rightArmPivot) {
      this.rightArmPivot.rotation.x = this.pitch;
      this.rightArmPivot.rotation.z = -swing;
    }

    // Idle breathing bob on torso (default pose)
    if (this.torsoMesh && !this.isWalking) {
      this.torsoMesh.position.y = 1.15 + Math.sin(performance.now() / 1000 * 2) * BREATHE_AMP;
    } else if (this.torsoMesh) {
      this.torsoMesh.position.y = 1.15;
    }

    // Reset torso rotation for clean slate before pose overrides
    if (this.torsoMesh) {
      this.torsoMesh.rotation.x = 0;
      this.torsoMesh.scale.y = 1;
    }

    // ── Detect airborne from Y position changes ──
    const dy = this.position.y - this.prevY;
    // Consider airborne if Y is changing noticeably (falling or rising)
    this.isGroundedRemote = Math.abs(dy) < 0.01;
    this.prevY = this.position.y;

    // ── Pose overrides (slide > crouch > airborne) ──
    if (this.isSliding) {
      // Slide pose: torso leans forward, body drops, legs extend
      if (this.torsoMesh) {
        this.torsoMesh.rotation.x = -0.4;
        this.torsoMesh.position.y -= 0.6;
      }
      if (this.rightLegPivot) {
        this.rightLegPivot.rotation.x = -0.8;
      }
      if (this.leftLegPivot) {
        this.leftLegPivot.rotation.x = 0.3;
      }
      // Arms tucked close
      if (this.rightArmPivot) {
        this.rightArmPivot.rotation.x = 0.3;
      }
      if (this.leftArmPivot) {
        this.leftArmPivot.rotation.x = 0.3;
      }
    } else if (this.isCrouching) {
      // Crouch pose: squish torso, move down, bend legs more
      if (this.torsoMesh) {
        this.torsoMesh.scale.y = 0.75;
        this.torsoMesh.position.y -= 0.3;
      }
      if (this.rightLegPivot) {
        this.rightLegPivot.rotation.x += -0.4;
      }
      if (this.leftLegPivot) {
        this.leftLegPivot.rotation.x += -0.4;
      }
    } else if (!this.isGroundedRemote) {
      // Airborne pose: tuck legs, raise arms slightly
      if (this.rightLegPivot) {
        this.rightLegPivot.rotation.x = -0.3;
      }
      if (this.leftLegPivot) {
        this.leftLegPivot.rotation.x = -0.3;
      }
      if (this.rightArmPivot) {
        this.rightArmPivot.rotation.x = -0.2;
      }
      if (this.leftArmPivot) {
        this.leftArmPivot.rotation.x = -0.2;
      }
    }

    // F-GP-001 — Apply ladder + mantle overlay on top of the procedural pose.
    // The animator is fed every tick (even when both bits are false) so its
    // ladderPhase damps cleanly and the falling-edge drift correction window
    // arms exactly when isMantling flips off.
    this.climbState.position = { x: this.position.x, y: this.position.y, z: this.position.z };
    this.climbAnimator.update(dt, this.climbState);
    const params = this.climbAnimator.getParams();

    if (params.isLadderClimb) {
      // Hand-over-hand ladder rhythm — arms alternate, legs alternate, body
      // rises slightly with each rung pull.
      const rungSwing = Math.sin(params.ladderPhase) * 0.6;
      if (this.rightArmPivot) {
        this.rightArmPivot.rotation.x = -1.2 + rungSwing;
        this.rightArmPivot.rotation.z = 0;
      }
      if (this.leftArmPivot) {
        this.leftArmPivot.rotation.x = -1.2 - rungSwing;
        this.leftArmPivot.rotation.z = 0;
      }
      if (this.rightLegPivot) {
        this.rightLegPivot.rotation.x = -rungSwing * 0.5;
      }
      if (this.leftLegPivot) {
        this.leftLegPivot.rotation.x = rungSwing * 0.5;
      }
      if (this.torsoMesh) {
        this.torsoMesh.position.y = 1.15 + Math.sin(params.ladderPhase) * params.ladderAmplitude;
        this.torsoMesh.rotation.x = 0;
      }
    }

    if (params.isMantle && this.torsoMesh) {
      // Forward lean peaks mid-mantle then returns to upright by t=1.
      this.torsoMesh.rotation.x = -params.mantleLeanRad;
    }

    // Update GLB animation mixer
    if (this.mixer) {
      this.mixer.update(dt);
    }
  }

  /**
   * Create mesh from a GLB skin file.
   * Falls back to procedural mesh if the skin isn't loaded.
   */
  async createFromSkin(
    scene: THREE.Scene,
    skinId: number,
    skinLoader: SkinLoader,
    fallbackColor: number,
    weaponSkinLoader?: WeaponSkinLoader | null,
  ): Promise<void> {
    if (this.mesh) {
      return;
    }

    this.skinId = skinId;
    this.weaponSkinLoader = weaponSkinLoader ?? this.weaponSkinLoader;

    // Try to clone a pre-loaded GLB skin
    const cloned = skinLoader.cloneSkin(skinId);
    if (cloned) {
      this.isGlbSkin = true;
      const group = new THREE.Group();
      group.add(cloned);

      // Enable shadows on all meshes
      cloned.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });

      // Set up animation mixer (animations will be applied when available)
      this.mixer = new THREE.AnimationMixer(cloned);

      // Add blob contact shadow
      this.blobShadow = createBlobShadow();
      group.add(this.blobShadow);

      group.position.copy(this.position);
      group.rotation.set(0, this.yaw, 0);
      this.mesh = group;
      scene.add(group);

      // Attach third-person weapon model
      attachTPPWeapon(group, this.currentWeapon, {
        skinId: this.currentWeaponSkinId,
        skinLoader: this.weaponSkinLoader,
      });

      // Bind animation pivots from the GLB scene graph.
      // Pivot nodes (__torso__, __right_arm_pivot__, etc.) were placed at Blender
      // export time by scripts/blender/character_builder.py:_build_pivots and are
      // preserved in the GLB node hierarchy by the glTF importer.
      this.bindPivots(cloned);
    } else {
      // Use procedural skin builders (unique character per skin)
      this.isGlbSkin = false;
      const skinGroup = buildSkinMesh(skinId, fallbackColor);
      skinGroup.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      const group = new THREE.Group();
      group.add(skinGroup);

      // Add blob contact shadow
      this.blobShadow = createBlobShadow();
      group.add(this.blobShadow);

      group.position.copy(this.position);
      group.rotation.set(0, this.yaw, 0);
      this.mesh = group;
      scene.add(group);

      // Attach third-person weapon model
      attachTPPWeapon(group, this.currentWeapon, {
        skinId: this.currentWeaponSkinId,
        skinLoader: this.weaponSkinLoader,
      });

      // Discover and bind animation pivots from the procedural skin
      this.bindPivots(skinGroup);
    }
  }

  /**
   * Traverse the procedural skin group and bind named pivots/torso
   * so the walk/pose animation system can drive them.
   */
  private bindPivots(skinGroup: THREE.Group): void {
    skinGroup.traverse((child) => {
      switch (child.name) {
        case '__right_arm_pivot__':
          this.rightArmPivot = child as THREE.Group;
          break;
        case '__left_arm_pivot__':
          this.leftArmPivot = child as THREE.Group;
          break;
        case '__right_leg_pivot__':
          this.rightLegPivot = child as THREE.Group;
          break;
        case '__left_leg_pivot__':
          this.leftLegPivot = child as THREE.Group;
          break;
        case '__torso__':
          this.torsoMesh = child as THREE.Mesh;
          break;
      }
    });
  }

  /** Remove mesh from scene and dispose geometry/materials. */
  removeMesh(scene: THREE.Scene): void {
    if (!this.mesh) return;

    scene.remove(this.mesh);

    // H22 fix: Remove blob shadow from group BEFORE traverse to prevent double-disposal
    if (this.blobShadow) {
      this.mesh.remove(this.blobShadow);
      releaseBlobShadow(this.blobShadow);
      this.blobShadow = null;
    }

    // F-GP-005: Detach TPP weapon and bomb props before the bare-traverse
    // disposal below. These holders contain meshes that reference process-wide
    // shared materials (gunMaterial / bladeMaterial / bombBodyMaterial /
    // bombLedMaterial); a generic traverse + dispose would kill them for every
    // other player still rendering. removeTPPWeapon / removeTPPBomb own the
    // guarded dispose path that skips the shared materials.
    removeTPPWeapon(this.mesh);
    removeTPPBomb(this.mesh);

    // Dispose all remaining children
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });

    // H20 fix: Uncache mixer root BEFORE nulling mesh reference
    if (this.mixer) {
      this.mixer.stopAllAction();
      this.mixer.uncacheRoot(this.mesh);
      this.mixer = null;
    }

    this.mesh = null;
    this.isGlbSkin = false;
    this.rightArmPivot = null;
    this.leftArmPivot = null;
    this.rightLegPivot = null;
    this.leftLegPivot = null;
    this.torsoMesh = null;
  }
}
